import { Component, OnInit, inject, effect, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';

import { BaaService } from '../../service/entity-services/baa.service';
import { Baa } from '../../objects/baa';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';

@Component({
    selector: 'app-baa-list',
    templateUrl: './baa-list.component.html',
    styleUrls: ['./baa-list.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      MatProgressBar,
      MatButtonToggleModule,
      MatTableModule,
      MatSortModule,
      MatIconModule,
      MatChipsModule,
      MatTooltipModule
    ]
  })
export class BaaListComponent implements OnInit {

  public filterService = inject(MyWorkFilterService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private baaService = inject(BaaService);

  public allBaas: Baa[] = [];
  public filteredBaas: Baa[] = [];

  waitingForFilterResults = false;
  isLoading = true;

  // Expose MyWorkFilter enum to template
  MyWorkFilter = MyWorkFilter;

  // Entity type display name
  entityTypeName = 'BAAs';

  displayedColumns: string[] = [
    'status',
    'coveredEntity',
    'businessAssociateName',
    'primaryOwner',
    'expirationDate',
    'description'
  ];

  constructor() {
    // Set up effect to watch for filter changes and user email resolution
    effect(() => {
      // Read signals to establish dependencies
      const activeFilters = this.filterService.activeFilters();
      const userEmail = this.filterService.currentUserEmail();

      // Apply filtering whenever filters, user email, or BAAs change
      if (this.allBaas.length > 0 && userEmail) {
        this.isLoading = false;
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  async ngOnInit() {
    // Subscribe to visible (non-archived, permission-filtered) BAAs
    this.baaService.visibleBaas$.subscribe(baas => {
      this.allBaas = baas;
      console.log(`Successfully retrieved ${this.allBaas.length} BAAs.`);

      // Only apply filters if we have BAAs and user email is resolved
      if (this.allBaas.length > 0 && this.filterService.currentUserEmail()) {
        this.isLoading = false;
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Applies current filter service filters to the BAAs list
   */
  private applyFilters(): void {
    this.filteredBaas = this.filterService.filterBaas(this.allBaas);
    console.log(`Filtered to ${this.filteredBaas.length} BAAs`);
  }

  /**
   * Handler for filter button toggle changes
   */
  userChangedFilter(event: any): void {
    const selectedValues: string[] = event.value || [];

    // Clear all filters first
    this.filterService.clearFilters();

    // Add each selected filter
    selectedValues.forEach(value => {
      switch (value) {
        case 'Owner':
          this.filterService.addFilter(MyWorkFilter.Owner);
          break;
        case 'ExpiringSoon':
          this.filterService.addFilter(MyWorkFilter.ExpiringSoon);
          break;
        case 'Expired':
          this.filterService.addFilter(MyWorkFilter.Expired);
          break;
        case 'NextReviewDateSoon':
          this.filterService.addFilter(MyWorkFilter.NextReviewDateSoon);
          break;
      }
    });
  }

  /**
   * Checks if a filter is currently active
   */
  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.isFilterActive(filter);
  }

  /**
   * Navigate to BAA detail page when row is clicked
   */
  onRowClicked(baa: Baa): void {
    this.router.navigate(['/securehome/baa', baa.baaNum]);
  }

  /**
   * Sort data when sort header is clicked
   */
  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.applyFilters();
      return;
    }

    this.filteredBaas = [...this.filteredBaas].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'coveredEntity': return this.compare(this.extractEntityName(a.coveredEntity), this.extractEntityName(b.coveredEntity), isAsc);
        case 'businessAssociateName': return this.compare(this.extractEntityName(a.businessAssociateName), this.extractEntityName(b.businessAssociateName), isAsc);
        case 'primaryOwner': return this.compare(this.extractOwnerName(a.primaryOwner), this.extractOwnerName(b.primaryOwner), isAsc);
        case 'expirationDate': return this.compare(a.expirationDate, b.expirationDate, isAsc);
        case 'description': return this.compare(a.description, b.description, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Format a date string for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    } catch {
      return dateString;
    }
  }

  /**
   * Extract a display name from an entity field that may be a string or object
   */
  extractEntityName(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value.name) return value.name;
    if (value.coveredEntity) return value.coveredEntity;
    if (value.businessAssociateName) return value.businessAssociateName;
    return '';
  }

  /**
   * Extract owner name from primaryOwner field
   * Handles string, object with userData, friendlyName, or email fallback
   */
  extractOwnerName(owner: any): string {
    if (!owner) return '';
    if (typeof owner === 'string') return owner;
    if (owner.userData) {
      const ud = owner.userData;
      if (ud.lastName && ud.firstName) return `${ud.lastName}, ${ud.firstName}`;
      if (ud.friendlyName) return ud.friendlyName;
      if (ud.email) return ud.email;
    }
    if (owner.friendlyName) return owner.friendlyName;
    if (owner.email) return owner.email;
    if (owner.name) return owner.name;
    return '';
  }

  /**
   * Check if a BAA is expired
   */
  isExpired(baa: Baa): boolean {
    if (!baa.expirationDate) return false;
    return new Date(baa.expirationDate) < new Date();
  }

  /**
   * Check if a BAA is expiring within 180 days
   */
  isExpiringSoon(baa: Baa): boolean {
    if (!baa.expirationDate) return false;
    const expDate = new Date(baa.expirationDate);
    const now = new Date();
    const daysUntil = (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntil >= 0 && daysUntil <= 180;
  }

  /**
   * Compare two values for sorting
   */
  private compare(a: any, b: any, isAsc: boolean): number {
    if (!a && !b) return 0;
    if (!a) return isAsc ? -1 : 1;
    if (!b) return isAsc ? 1 : -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
